<template>
  <div class="attachments-container">
    <!-- File selection button -->
    <b-row class="mb-3">
      <b-col>
        <b-button
          variant="primary"
          size="sm"
          @click="openFileUpload"
          :disabled="!areFieldsEditable"
        >
          <i class="flaticon2-plus"></i> Chọn tệp đính kèm
        </b-button>
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          @change="handleFileSelect"
          accept=".jpg,.jpeg,.png,.pdf"
          multiple
        />
      </b-col>

      <!-- Thông báo hướng dẫn -->
      <small
        class="d-flex align-items-center justify-content-center"
        style="column-gap: 4px"
      >
        <i class="flaticon2-information text-primary"></i>
        <p class="mb-0 text-primary">
          Tối đa 3 tệp, mỗi tệp không quá 10MB. Định dạng: JPG, PNG, PDF
        </p>
      </small>
    </b-row>

    <!-- Selected files preview table -->
    <b-table
      v-if="selectedFiles.length > 0"
      :items="selectedFiles"
      :fields="selectedFileFields"
      class="table-bordered table-hover mb-3"
      :small="true"
    >
      <template v-slot:cell(fileName)="row">
        {{ row.item.file.name }}
      </template>

      <template v-slot:cell(fileSize)="row">
        {{ formatFileSizeMB(row.item.file.size) }}
      </template>

      <template v-slot:cell(preview)="row">
        <!-- Ảnh có thể nhấn vào để xem -->
        <div
          v-if="row.item.previewUrl && isImageFile(row.item.file.type)"
          class="preview-thumbnail d-flex justify-content-center"
        >
          <img
            :src="row.item.previewUrl"
            class="img-thumbnail cursor-pointer"
            style="max-height: 100px"
            @click="previewFile(row.item)"
            v-b-tooltip.hover
            title="Nhấn để xem ảnh"
          />
        </div>
        <!-- PDF có thể nhấn vào để xem -->
        <div
          v-else-if="isPdfFile(row.item.file.type)"
          class="preview-thumbnail d-flex justify-content-center"
        >
          <span
            class="cursor-pointer"
            @click="previewFile(row.item)"
            v-b-tooltip.hover
            title="Nhấn để xem PDF"
          >
            <i class="flaticon2-file text-danger"></i> PDF
          </span>
        </div>
        <!-- Other file types -->
        <div v-else class="d-flex justify-content-center">
          <span class="text-muted"> <i class="flaticon2-file"></i> File </span>
        </div>
      </template>

      <template v-slot:cell(actions)="row">
        <div class="d-flex justify-content-center">
          <b-button
            variant="link"
            size="sm"
            class="p-0 mr-2"
            @click="previewFile(row.item)"
            v-b-tooltip.hover
            title="Xem"
          >
            <i class="flaticon-eye text-primary"></i>
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            @click="removeSelectedFile(row.index)"
            v-b-tooltip.hover
            title="Xóa"
          >
            <i class="flaticon2-trash text-danger"></i>
          </b-button>
        </div>
      </template>

      <template v-slot:empty>
        <div class="text-center py-3">
          <p class="mb-0">Chưa có tệp đính kèm nào được chọn</p>
        </div>
      </template>
    </b-table>

    <!-- Upload all button -->
    <b-row v-if="selectedFiles.length > 0" class="mb-4">
      <b-col class="d-flex justify-content-end">
        <b-button
          variant="success"
          size="sm"
          @click="uploadAllFiles"
          :disabled="isUploading || !areFieldsEditable"
        >
          <b-spinner v-if="isUploading" small></b-spinner>
          <i v-else class="flaticon2-arrow-up"></i> Upload tất cả
        </b-button>
      </b-col>
    </b-row>

    <!-- Uploaded files table -->
    <h6 class="font-weight-bold mb-3">Tệp đính kèm đã tải lên</h6>
    <b-table
      :items="attachments"
      :fields="attachmentFields"
      class="table-bordered table-hover"
      :small="true"
    >
      <template v-slot:cell(fileName)="row">
        <a href="#" @click.prevent="downloadAttachment(row.item)">
          {{ row.item.fileName }}
        </a>
      </template>

      <template v-slot:cell(uploadDate)="row">
        {{ formatDate(row.item.uploadDate) }}
      </template>

      <template v-slot:cell(actions)="row">
        <div class="d-flex justify-content-center">
          <b-button
            variant="link"
            size="sm"
            class="p-0 mr-2"
            @click="previewUploadedFile(row.item)"
            v-b-tooltip.hover
            title="Xem"
          >
            <i class="flaticon-eye text-primary"></i>
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0 mr-2"
            @click="downloadAttachment(row.item)"
            v-b-tooltip.hover
            title="Tải xuống"
          >
            <i class="flaticon2-download text-primary"></i>
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            @click="deleteAttachment(row.item)"
            v-b-tooltip.hover
            title="Xóa"
            :disabled="!areFieldsEditable"
          >
            <i class="flaticon2-trash text-danger"></i>
          </b-button>
        </div>
      </template>

      <template v-slot:empty>
        <div class="text-center py-3">
          <p class="mb-0">Chưa có tệp đính kèm nào</p>
        </div>
      </template>
    </b-table>

    <!-- Preview modal -->
    <b-modal
      v-model="previewModal.show"
      :title="previewModal.title"
      size="lg"
      centered
      hide-footer
      @hidden="closePreview"
    >
      <div class="text-center">
        <!-- Hiển thị ảnh -->
        <img
          v-if="previewModal.type === 'image'"
          :src="previewModal.url"
          class="img-fluid"
          alt="Preview"
        />
        <!-- Hiển thị PDF -->
        <iframe
          v-else-if="previewModal.type === 'pdf'"
          :src="previewModal.url"
          width="100%"
          height="700"
          frameborder="0"
        ></iframe>
        <!-- Hiển thị thông báo tải xuống cho file khác -->
        <div v-else>
          <p>Nhấn nút bên dưới để tải xuống và xem file</p>
          <b-button
            variant="primary"
            @click="downloadAttachment(previewModal.file)"
          >
            <i class="flaticon2-download"></i> Tải xuống
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import {
  formatFileSizeMB,
  isImageFile,
  isPdfFile,
} from '@/utils/common-upload-files';
import { TestDriveFilesService } from '@/core/services/test-drives';

export default {
  name: 'AttachmentsTab',
  props: {
    testDriveId: {
      type: String,
      required: true,
    },

    areFieldsEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Tables
      selectedFileFields: [
        {
          key: 'fileName',
          label: 'Tên tệp',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'fileSize',
          label: 'Kích thước',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'preview',
          label: 'Xem trước',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'actions',
          label: 'Thao tác',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],

      attachmentFields: [
        {
          key: 'fileName',
          label: 'Tên tệp',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'uploadDate',
          label: 'Ngày tải lên',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'actions',
          label: 'Thao tác',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],

      // States
      isUploading: false,

      attachments: [],
      selectedFiles: [],

      // Dữ liệu cho modal xem trước
      previewModal: {
        show: false,
        title: '',
        url: '',

        type: '', // 'image', 'pdf', 'other'
        file: null,
      },

      //   Contants
      MAX_FILES_UPLOADED: 3,
      MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    };
  },

  async mounted() {
    // Load existing files when component mounts
    await this.loadAttachments();
  },

  methods: {
    formatDate,
    formatFileSizeMB,
    isImageFile,
    isPdfFile,

    // Load attachments from server
    async loadAttachments() {
      try {
        const { data } = await TestDriveFilesService.getFiles(this.testDriveId);
        this.attachments = data.data.map((file) => ({
          id: file.id,
          fileName: file.fileName,
          uploadDate: file.createdAt,
        }));
      } catch (error) {
        console.error('Error loading attachments:', error);
      }
    },

    // Open file upload
    openFileUpload() {
      this.$refs.fileInput.click();
    },

    // Xử lý khi người dùng chọn file
    handleFileSelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Kiểm tra số lượng file
      if (files.length > this.MAX_FILES_UPLOADED) {
        makeToastFaile('Chỉ được phép chọn tối đa 3 tệp cùng lúc');
        this.$refs.fileInput.value = '';
        return;
      }

      // Kiểm tra kích thước từng file (10MB = 10 * 1024 * 1024 bytes)
      const oversizedFiles = [];

      for (let i = 0; i < files.length; i++) {
        if (files[i].size > this.MAX_FILE_SIZE) {
          oversizedFiles.push(files[i].name);
        }
      }

      if (oversizedFiles.length > 0) {
        makeToastFaile(
          `Các tệp sau vượt quá giới hạn 10MB: ${oversizedFiles.join(', ')}`,
        );
        this.$refs.fileInput.value = '';
        return;
      }

      const validation = TestDriveFilesService.validateFiles(files);

      validation.errors.forEach((error) => {
        makeToastFaile(error);
      });

      // Process valid files
      validation.validFiles.forEach((file) => {
        const previewUrl = TestDriveFilesService.createPreviewUrl(file);

        this.selectedFiles.push({
          file: file,
          previewUrl: previewUrl,
        });
      });

      // Reset file input
      this.$refs.fileInput.value = '';
    },

    // Xem trước file đã chọn
    previewFile(fileObj) {
      if (!fileObj || !fileObj.previewUrl) return;

      this.previewModal.title = fileObj.file.name;
      this.previewModal.url = fileObj.previewUrl;

      this.previewModal.type = TestDriveFilesService.getFileType(fileObj.file);
      this.previewModal.show = true;
    },

    // Xem trước file đã upload

    async previewUploadedFile(attachment) {
      try {
        const response = await TestDriveFilesService.previewFile(
          this.testDriveId,
          attachment.fileName,
        );

        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        });
        const url = URL.createObjectURL(blob);

        this.previewModal.title = attachment.fileName;
        this.previewModal.url = url;
        this.previewModal.type = TestDriveFilesService.getFileType(
          attachment.fileName,
        );

        if (this.previewModal.type === 'other') {
          this.previewModal.file = attachment;
        }

        this.previewModal.show = true;
      } catch (error) {
        console.error('Error previewing file:', error);
        makeToastFaile('Không thể xem trước tệp này');
      }
    },

    // Đóng modal xem trước
    closePreview() {
      if (this.previewModal.url && this.previewModal.url.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewModal.url);
      }
      this.previewModal.url = '';
      this.previewModal.title = '';
      this.previewModal.type = '';
      this.previewModal.file = null;
    },

    // Xóa file khỏi danh sách chờ upload
    removeSelectedFile(index) {
      // Giải phóng URL object để tránh memory leak
      if (this.selectedFiles[index].previewUrl) {
        URL.revokeObjectURL(this.selectedFiles[index].previewUrl);
      }

      // Xóa file khỏi danh sách
      this.selectedFiles.splice(index, 1);
    },

    // Upload tất cả các file đã chọn
    async uploadAllFiles() {
      if (this.selectedFiles.length === 0) {
        makeToastFaile('Không có tệp nào được chọn để tải lên');
        return;
      }

      if (this.selectedFiles.length > this.MAX_FILES_UPLOADED) {
        makeToastFaile('Chỉ được phép chọn tối đa 3 tệp cùng lúc');
        return;
      }

      this.isUploading = true;

      try {
        const files = this.selectedFiles.map((fileObj) => fileObj.file);

        const { data } = await TestDriveFilesService.uploadFiles(
          this.testDriveId,
          files,
        );

        const uploadedFiles = data.data.map((file) => {
          return {
            id: file.id,
            fileName: file.fileName,
            uploadDate: file.createdAt,
          };
        });

        this.attachments = [...uploadedFiles, ...this.attachments];

        // Xóa tất cả file đã chọn sau khi upload thành công
        this.clearSelectedFiles();

        makeToastSuccess('Tải lên tệp đính kèm thành công');
      } catch (error) {
        console.error('Error uploading files:', error);
        makeToastFaile('Không thể tải lên tệp đính kèm');
      } finally {
        this.isUploading = false;
      }
    },

    // Xóa tất cả file đã chọn
    clearSelectedFiles() {
      // Giải phóng tất cả URL objects
      this.selectedFiles.forEach((fileObj) => {
        if (fileObj.previewUrl) {
          URL.revokeObjectURL(fileObj.previewUrl);
        }
      });

      // Xóa tất cả file
      this.selectedFiles = [];
    },

    // Download attachment
    async downloadAttachment(attachment) {
      try {
        const response = await TestDriveFilesService.downloadFile(
          attachment.id,
        );

        // Tạo URL và download file
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', attachment.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        makeToastSuccess('Tải xuống thành công');
      } catch (error) {
        console.error('Error downloading attachment:', error);
        makeToastFaile('Không thể tải xuống tệp đính kèm');
      }
    },

    // Delete attachment
    async deleteAttachment(attachment) {
      try {
        await TestDriveFilesService.deleteFile(attachment.id);

        this.handleAttachmentDeleted(attachment.id);

        makeToastSuccess('Xóa tệp đính kèm thành công');
      } catch (error) {
        console.error('Error deleting attachment:', error);
        makeToastFaile('Không thể xóa tệp đính kèm');
      }
    },

    handleAttachmentDeleted(attachmentId) {
      if (!this.areFieldsEditable) {
        return;
      }

      this.attachments = this.attachments.filter((a) => a.id !== attachmentId);
    },
  },

  // Cleanup khi component bị hủy
  beforeDestroy() {
    // Giải phóng tất cả URL objects để tránh memory leak
    this.selectedFiles.forEach((fileObj) => {
      if (fileObj.previewUrl) {
        URL.revokeObjectURL(fileObj.previewUrl);
      }
    });

    if (this.previewModal.url && this.previewModal.url.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewModal.url);
    }
  },
};
</script>

<style lang="scss" scoped>
.attachments-container {
  min-height: 400px;
}

.cursor-pointer {
  cursor: pointer;
}

.preview-thumbnail {
  &:hover {
    opacity: 0.8;
  }

  img {
    object-fit: contain;
    max-height: 100px; /* Tăng kích thước ảnh xem trước */
    width: auto;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05);
    }
  }
}
</style>
